import { formatDateToISO } from "./date";
import { TIMEZONE_OPTIONS, FALLBACK_TIMEZONE, convertISTToTimezone, getTimezoneAbbreviation, } from "./timezone";
function extractTimeStr(slotStart) {
    if (typeof slotStart === "string" && slotStart.includes("T")) {
        const d = new Date(slotStart);
        return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
    }
    if (typeof slotStart === "number" || !Number.isNaN(Number(slotStart))) {
        const d = new Date(Number(slotStart));
        return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
    }
    return slotStart;
}
export function formatSlotTime(slot, date, selectedTimezone) {
    const timeStr = extractTimeStr(slot.start);
    const dateStr = formatDateToISO(date);
    return convertISTToTimezone(timeStr, dateStr, selectedTimezone);
}
export function getTimezoneLabel(selectedTimezone) {
    const tz = TIMEZONE_OPTIONS.find((t) => t.value === selectedTimezone);
    if (tz)
        return tz.label;
    return getTimezoneAbbreviation(selectedTimezone);
}
export function parseFormattedTime(formattedTime) {
    const match = formattedTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) {
        const [hours, minutes] = formattedTime.split(":").map(Number);
        return (hours || 0) * 60 + (minutes || 0);
    }
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    if (period === "AM") {
        if (hours === 12)
            hours = 0;
    }
    else {
        if (hours !== 12)
            hours += 12;
    }
    return hours * 60 + minutes;
}
export function getConvertedSlotTime(slot, date, selectedTimezone) {
    const timeStr = extractTimeStr(slot.start);
    const dateStr = formatDateToISO(date);
    return convertISTToTimezone(timeStr, dateStr, selectedTimezone);
}
export function getCurrentTimeInTimezone(selectedTimezone) {
    const now = new Date();
    const timeInTz = now.toLocaleTimeString("en-US", {
        timeZone: selectedTimezone,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
    return parseFormattedTime(timeInTz);
}
export function isSelectedDateToday(date, selectedTimezone) {
    const now = new Date();
    const todayInTz = now.toLocaleDateString("en-CA", {
        timeZone: selectedTimezone,
    });
    const selectedDateStr = formatDateToISO(date);
    return todayInTz === selectedDateStr;
}
export function parseRawSlotTime(timeStr) {
    if (timeStr.includes("T")) {
        return new Date(timeStr).getTime();
    }
    if (!Number.isNaN(Number(timeStr))) {
        return Number(timeStr);
    }
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
}
export function calculateConsultationDuration(sortedSlots) {
    if (sortedSlots.length === 0)
        return 0;
    const firstSlot = sortedSlots[0];
    const startTime = parseRawSlotTime(firstSlot.start);
    const endTime = parseRawSlotTime(firstSlot.end);
    let diffMinutes;
    if (firstSlot.start.includes("T") || !Number.isNaN(Number(firstSlot.start))) {
        diffMinutes = Math.round((endTime - startTime) / (1000 * 60));
    }
    else {
        diffMinutes = endTime - startTime;
    }
    return diffMinutes > 0 ? diffMinutes : 0;
}
function parseInstant(iso) {
    if (!iso)
        return null;
    const ms = new Date(iso).getTime();
    return Number.isNaN(ms) ? null : ms;
}
export function getSlotStartInstantMs(slot) {
    return (parseInstant(slot.patientFromDateTimeIso) ??
        parseInstant(slot.fromDateTimeIso));
}
export function formatInstantInTimezone(ms, timezone) {
    try {
        return new Date(ms).toLocaleTimeString("en-US", {
            timeZone: timezone,
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    }
    catch {
        return new Date(ms).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    }
}
function dateKeyInTimezone(ms, timezone) {
    try {
        return new Date(ms).toLocaleDateString("en-CA", { timeZone: timezone });
    }
    catch {
        return new Date(ms).toLocaleDateString("en-CA");
    }
}
function resolveSlotDateStr(slot, fallbackDate) {
    if (slot.appointmentDate)
        return slot.appointmentDate;
    if (typeof slot.start === "string" && slot.start.includes("T")) {
        return slot.start.split("T")[0];
    }
    return formatDateToISO(fallbackDate ?? new Date());
}
export function getSlotHourInTimezone(slot, timezone, fallbackDate) {
    const ms = getSlotStartInstantMs(slot);
    if (ms !== null) {
        try {
            return Number.parseInt(new Date(ms).toLocaleString("en-US", {
                timeZone: timezone,
                hour: "numeric",
                hour12: false,
            }), 10);
        }
        catch {
        }
    }
    const converted = convertISTToTimezone(extractTimeStr(slot.start), resolveSlotDateStr(slot, fallbackDate), timezone);
    return Math.floor(parseFormattedTime(converted) / 60);
}
function compareDateKeys(a, b) {
    if (a === b)
        return 0;
    return a < b ? -1 : 1;
}
export function resolveSlotDisplayTimes(slot, patientTimezone, fallbackDate) {
    const clinicTimezone = slot.appointmentTimezone || slot.patientTimezone || FALLBACK_TIMEZONE;
    const resolvedPatientTz = patientTimezone || slot.patientTimezone || clinicTimezone;
    const startMs = getSlotStartInstantMs(slot);
    const endMs = parseInstant(slot.patientToDateTimeIso) ?? parseInstant(slot.toDateTimeIso);
    let patientStart;
    let patientEnd;
    let clinicStart;
    let clinicEnd;
    let dayOffset = 0;
    if (startMs !== null) {
        patientStart = formatInstantInTimezone(startMs, resolvedPatientTz);
        clinicStart = formatInstantInTimezone(startMs, clinicTimezone);
        patientEnd =
            endMs !== null
                ? formatInstantInTimezone(endMs, resolvedPatientTz)
                : patientStart;
        clinicEnd =
            endMs !== null
                ? formatInstantInTimezone(endMs, clinicTimezone)
                : clinicStart;
        dayOffset = compareDateKeys(dateKeyInTimezone(startMs, resolvedPatientTz), dateKeyInTimezone(startMs, clinicTimezone));
    }
    else {
        const dateStr = resolveSlotDateStr(slot, fallbackDate);
        patientStart = convertISTToTimezone(extractTimeStr(slot.start), dateStr, resolvedPatientTz);
        patientEnd = convertISTToTimezone(extractTimeStr(slot.end), dateStr, resolvedPatientTz);
        clinicStart = convertISTToTimezone(extractTimeStr(slot.start), dateStr, clinicTimezone);
        clinicEnd = convertISTToTimezone(extractTimeStr(slot.end), dateStr, clinicTimezone);
    }
    return {
        patientStart,
        patientEnd,
        clinicStart,
        clinicEnd,
        patientTimezone: resolvedPatientTz,
        clinicTimezone,
        showClinicTime: clinicStart !== patientStart || dayOffset !== 0,
        dayOffset,
        startInstantMs: startMs,
    };
}
export function formatTimeRange(start, end) {
    const startMeridiem = /\s*(AM|PM)\s*$/i.exec(start);
    const endMeridiem = /\s*(AM|PM)\s*$/i.exec(end);
    if (startMeridiem &&
        endMeridiem &&
        startMeridiem[1].toUpperCase() === endMeridiem[1].toUpperCase()) {
        return `${start.slice(0, startMeridiem.index)} \u2013 ${end}`;
    }
    return `${start} \u2013 ${end}`;
}
export function isSlotInPast(slot, fallbackDate) {
    const ms = getSlotStartInstantMs(slot);
    if (ms !== null)
        return ms < Date.now();
    try {
        const timeStr = extractTimeStr(slot.start);
        const padded = timeStr.length < 5 ? timeStr.padStart(5, "0") : timeStr;
        const dateStr = resolveSlotDateStr(slot, fallbackDate);
        return new Date(`${dateStr}T${padded}:00+05:30`).getTime() < Date.now();
    }
    catch {
        return false;
    }
}
